<template>
  <div class="mod-inventory-warehouse-visual">
    <el-form :inline="true" class="visual-toolbar">
      <el-form-item label="仓库">
        <el-select v-model="query.wareHouseId" clearable filterable placeholder="全部仓库" size="small" style="width:180px">
          <el-option v-for="w in warehouseOptions" :key="w.wareHouseId" :label="w.wareHouseName" :value="w.wareHouseId" />
        </el-select>
      </el-form-item>
      <el-form-item label="物料编码">
        <el-input v-model="query.materialCode" clearable size="small" placeholder="模糊查询" style="width:140px" />
      </el-form-item>
      <el-form-item><el-checkbox v-model="query.hideZero">隐藏零库存物料</el-checkbox></el-form-item>
      <el-form-item><el-checkbox v-model="query.onlyBelowSafety">仅查低于安全库存</el-checkbox></el-form-item>
      <el-form-item><el-checkbox v-model="viewOptions.hideEmptyLocation">隐藏空库位</el-checkbox></el-form-item>
      <el-form-item><el-checkbox v-model="viewOptions.onlyWarningLocation">仅显示预警库位</el-checkbox></el-form-item>
      <el-form-item>
        <el-button v-if="isAuth('inventory:warehouseVisual:list')" type="primary" size="small" :loading="loading" @click="loadLayout">刷新</el-button>
      </el-form-item>
    </el-form>
    <div class="visual-legend">
      <span class="legend-item"><i class="dot dot-normal" />正常</span>
      <span class="legend-item"><i class="dot dot-warning" />全仓低于安全库存</span>
      <span class="legend-item"><i class="dot dot-empty" />空库位</span>
      <span class="legend-hint">物料全仓可用量低于安全库存时，含该物料的库位标红</span>
    </div>
    <el-row :gutter="12" class="summary-row" v-if="summary">
      <el-col :span="4"><el-card shadow="never">仓库数 {{ summary.warehouseCount || 0 }}</el-card></el-col>
      <el-col :span="4"><el-card shadow="never">库位数 {{ summary.locationCount || 0 }}</el-card></el-col>
      <el-col :span="4"><el-card shadow="never">预警库位 {{ summary.warningLocationCount || 0 }}</el-card></el-col>
      <el-col :span="4"><el-card shadow="never">预警SKU {{ summary.warningSkuCount || 0 }}</el-card></el-col>
      <el-col :span="4"><el-card shadow="never">库存总量 {{ formatQty(summary.quantityTotal) }}</el-card></el-col>
      <el-col :span="4"><el-card shadow="never">可用总量 {{ formatQty(summary.usableTotal) }}</el-card></el-col>
    </el-row>
    <div v-loading="loading" class="warehouse-board-wrap">
      <el-empty v-if="!loading && displayWarehouses.length === 0" description="暂无仓库或库位数据" />
      <div v-for="wh in displayWarehouses" :key="wh.wareHouseId" class="warehouse-panel">
        <div class="warehouse-header">
          <span class="warehouse-title">{{ wh.wareHouseName }}</span>
          <span class="warehouse-meta">库位 {{ wh.locationCount }} · 预警 {{ wh.warningLocationCount }} · 库存 {{ formatQty(wh.quantityTotal) }} / 可用 {{ formatQty(wh.usableTotal) }}</span>
        </div>
        <div v-for="area in wh.areas" :key="area.areaId" class="area-block">
          <div class="area-title">{{ area.areaName }}</div>
          <div class="location-grid">
            <div v-for="loc in filterLocations(area.locations)" :key="loc.locationId" class="location-card" :class="'status-' + loc.status" @click.stop="openLocationDetail(loc)">
              <div class="loc-head">
                <span class="loc-name" :title="loc.locationName">{{ loc.locationName }}</span>
                <el-tag v-if="loc.status === 'warning'" type="danger" size="mini">预警</el-tag>
                <el-tag v-else-if="loc.status === 'empty'" type="info" size="mini">空</el-tag>
              </div>
              <div class="loc-qty">
                <div><span class="lbl">库存</span> {{ formatQty(loc.quantityTotal) }}</div>
                <div><span class="lbl">可用</span> {{ formatQty(loc.usableTotal) }}</div>
              </div>
              <div class="loc-sku">SKU {{ loc.skuCount }}<span v-if="loc.warningSkuCount"> · 预警 {{ loc.warningSkuCount }}</span></div>
              <div v-if="loc.materials && loc.materials.length" class="loc-preview">
                <span v-for="m in loc.materials.slice(0, 3)" :key="m.materialId" class="mat-chip" :class="{ 'mat-warn': m.belowSafety }">{{ m.materialCode }}</span>
                <span v-if="loc.materials.length > 3" class="mat-more">+{{ loc.materials.length - 3 }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <el-dialog :title="dialogTitle" :visible.sync="dialogVisible" width="540px" top="8vh" append-to-body @closed="activeLocation = null">
      <div v-if="activeLocation">
        <p class="loc-dialog-path">{{ activeLocation.wareHousePath }}</p>
        <div class="loc-dialog-summary">
          <span>库存 {{ formatQty(activeLocation.quantityTotal) }}</span>
          <span>可用 {{ formatQty(activeLocation.usableTotal) }}</span>
          <span>SKU {{ activeLocation.skuCount }}</span>
        </div>
        <el-table :data="activeLocation.materials" border size="small" max-height="420">
          <el-table-column prop="materialCode" label="编码" min-width="100" />
          <el-table-column prop="materialName" label="名称" min-width="120" show-overflow-tooltip />
          <el-table-column prop="usableQuantity" label="可用" width="80" align="right" />
          <el-table-column prop="safetyStock" label="安全库存" width="88" align="right" />
          <el-table-column label="状态" width="72" align="center">
            <template slot-scope="s">
              <el-tag v-if="s.row.belowSafety" type="danger" size="mini">预警</el-tag>
              <el-tag v-else type="success" size="mini">正常</el-tag>
            </template>
          </el-table-column>
        </el-table>
      </div>
    </el-dialog>
  </div>
</template>
<script>
import request from '../../api/request'
import { authMixin } from './inventoryShared'
export default {
  name: 'WarehouseVisual',
  mixins: [authMixin],
  data() {
    return {
      query: { wareHouseId: '', materialCode: '', hideZero: true, onlyBelowSafety: false },
      viewOptions: { hideEmptyLocation: false, onlyWarningLocation: false },
      warehouseOptions: [], layout: null, loading: false, dialogVisible: false, activeLocation: null
    }
  },
  computed: {
    summary() { return (this.layout && this.layout.summary) || null },
    displayWarehouses() { return (this.layout && this.layout.warehouses) || [] },
    dialogTitle() { return this.activeLocation ? '库位：' + this.activeLocation.locationName : '库位明细' }
  },
  created() { this.loadOptions(); this.loadLayout() },
  methods: {
    loadOptions() {
      request.get('/inventory/warehouseVisual/list').then(res => { this.warehouseOptions = res.data || [] }).catch(() => {})
    },
    loadLayout() {
      this.loading = true
      request.get('/inventory/warehouseVisual/layout', { params: this.query }).then(res => {
        this.layout = res.data || { summary: {}, warehouses: [] }
      }).catch(() => { this.layout = { summary: {}, warehouses: [] } }).finally(() => { this.loading = false })
    },
    filterLocations(locations) {
      let list = locations || []
      if (this.viewOptions.hideEmptyLocation) list = list.filter(item => item.status !== 'empty')
      if (this.viewOptions.onlyWarningLocation) list = list.filter(item => item.status === 'warning')
      return list
    },
    formatQty(value) {
      const n = Number(value || 0)
      return Number.isNaN(n) ? '0' : n.toLocaleString(undefined, { maximumFractionDigits: 2 })
    },
    openLocationDetail(loc) { this.activeLocation = loc; this.dialogVisible = true }
  }
}
</script>
<style scoped>
.visual-toolbar { margin-bottom: 8px; }
.visual-legend { margin-bottom: 12px; font-size: 13px; color: #606266; }
.legend-item { margin-right: 20px; }
.dot { display: inline-block; width: 12px; height: 12px; border-radius: 2px; margin-right: 6px; vertical-align: middle; }
.dot-normal { background: #67c23a; }
.dot-warning { background: #f56c6c; }
.dot-empty { background: #dcdfe6; }
.legend-hint { color: #909399; margin-left: 8px; }
.summary-row { margin-bottom: 16px; }
.summary-row .el-card { text-align: center; font-size: 13px; }
.warehouse-panel { margin-bottom: 28px; border: 1px solid #e4e7ed; border-radius: 8px; overflow: hidden; background: #fafafa; }
.warehouse-header { display: flex; align-items: baseline; justify-content: space-between; padding: 12px 16px; background: linear-gradient(90deg, #409eff 0%, #66b1ff 100%); color: #fff; }
.warehouse-title { font-size: 18px; font-weight: 600; }
.warehouse-meta { font-size: 13px; }
.area-block { padding: 12px 16px 4px; }
.area-title { font-size: 14px; font-weight: 600; margin-bottom: 10px; padding-left: 8px; border-left: 3px solid #409eff; }
.location-grid { display: flex; flex-wrap: wrap; gap: 12px; }
.location-card { width: 200px; min-height: 120px; padding: 10px 12px; border-radius: 8px; border: 2px solid transparent; background: #fff; cursor: pointer; }
.location-card.status-normal { border-color: #b3e19d; background: #f0f9eb; }
.location-card.status-warning { border-color: #fbc4c4; background: #fef0f0; }
.location-card.status-empty { border-color: #e4e7ed; background: #f5f7fa; }
.loc-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 8px; }
.loc-name { font-weight: 600; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 120px; }
.loc-qty { font-size: 13px; line-height: 1.6; }
.loc-qty .lbl { color: #909399; margin-right: 4px; }
.loc-sku { font-size: 12px; color: #909399; margin-top: 4px; }
.loc-preview { margin-top: 8px; display: flex; flex-wrap: wrap; gap: 4px; }
.mat-chip { font-size: 11px; padding: 2px 6px; border-radius: 4px; background: #ecf5ff; color: #409eff; }
.mat-chip.mat-warn { background: #fde2e2; color: #f56c6c; }
.mat-more { font-size: 11px; color: #909399; }
.loc-dialog-path { margin: 0 0 8px; color: #606266; font-size: 13px; }
.loc-dialog-summary { margin-bottom: 12px; font-size: 13px; }
.loc-dialog-summary span { margin-right: 16px; }
</style>
